import { FunctionComponent, useState } from 'react'
import { Daycare, DaycareType } from '@/types/Daycare'
import styles from './daycareNoMergerSettings.module.scss'
import { useMutation } from '@apollo/client'
import { Table, TableBody, TableCell, TableRow } from '@mui/material'
import UserInput from '@/components/_custom/UserInput/userInput'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { CHANGE_NO_MERGER_PARAMETERS } from '@/modules/API/daycare/daycareHandler'
import Checkbox from '@/components/_custom/Checkbox/Checkbox'
import { useAuth } from '@/modules/context/AuthContext'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface DaycareMergerSettingsProps {
    daycare: Daycare
    disabled: boolean
}

const DaycareNoMergerSettings: FunctionComponent<DaycareMergerSettingsProps> = ({
    daycare,
    disabled
}: DaycareMergerSettingsProps) => {
    const { t } = useTranslation()

    const { fetchCurrentDaycare } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [newSlotNumber, setNewSlotNumber] = useState<number>(daycare.limitOfSlot)
    const [newWeeksNumber, setNewWeeksNumber] = useState<number>(daycare.limitByWeek)
    const [newExceedLimit, setNewExceedLimit] = useState<boolean>(daycare.exceedLimit)

    const [slotNumber, setSlotNumber] = useState<number>(daycare.limitOfSlot)
    const [weeksNumber, setWeeksNumber] = useState<number>(daycare.limitByWeek)
    const [exceedLimit, setExceedLimit] = useState<boolean>(daycare.exceedLimit)

    const [changeMinSlot] = useMutation(CHANGE_NO_MERGER_PARAMETERS, {
        variables: {
            daycareId: daycare.id,
            limitByWeek: weeksNumber,
            limitOfSlot: slotNumber,
            exceedLimit: exceedLimit
        },
        onCompleted: (): void => {
            fetchCurrentDaycare()

            setNewSlotNumber(slotNumber)
            setNewWeeksNumber(weeksNumber)
            setNewExceedLimit(exceedLimit)

            showNotificationSuccess(t('reservationSettingsUpdated'))
        },
        onError: (): void => {
            showNotificationError(t('errorUpdatingReservationSettings'))
        }
    })

    const isRpeOrLaep: boolean = daycare.type === DaycareType.rpe || daycare.type === DaycareType.laep

    const valid: boolean = slotNumber >= 0 && slotNumber <= 365 && weeksNumber >= 1 && weeksNumber <= 52

    const changed: boolean =
        slotNumber !== newSlotNumber || weeksNumber !== newWeeksNumber || exceedLimit !== newExceedLimit

    return (
        <Table className={styles.SettingsTable}>
            <TableBody>
                <TableRow>
                    <TableCell className={styles.RowText} colSpan={2}>
                        <Checkbox
                            text={t('limitCanBeExceeded')}
                            checked={exceedLimit}
                            onChange={() => setExceedLimit(!exceedLimit)}
                            disabled={!disabled}
                        />
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell className={styles.RowText}>{t('limitOf')}</TableCell>
                    <TableCell>
                        <UserInput
                            type='number'
                            value={slotNumber.toString()}
                            disabled={!disabled}
                            setValue={(value: string) => setSlotNumber(Number(value))}
                            className={styles.UserInput}
                            endAdornment={
                                slotNumber > 1
                                    ? isRpeOrLaep
                                        ? t('animations')
                                        : t('slots')
                                    : isRpeOrLaep
                                      ? t('animation')
                                      : t('slot')
                            }
                            maxNumber={365}
                            minNumber={1}
                            verify={'number'}
                        />
                    </TableCell>
                </TableRow>
                <TableRow>
                    <TableCell className={styles.RowText}>{t('on')}</TableCell>
                    <TableCell>
                        <UserInput
                            type='number'
                            value={weeksNumber.toString()}
                            disabled={!disabled}
                            setValue={(value: string) => setWeeksNumber(Number(value))}
                            className={styles.UserInput}
                            endAdornment={weeksNumber > 1 ? t('weeks') : t('week')}
                            maxNumber={52}
                            minNumber={1}
                            verify={'number'}
                        />
                    </TableCell>
                </TableRow>
                {disabled && (
                    <TableRow>
                        <TableCell align={'center'} colSpan={3} className={styles.HideBorder}>
                            <ButtonTextIcon
                                type={'update'}
                                onClick={changeMinSlot}
                                disabled={!changed || !valid}
                                className={styles.Button}
                            />
                        </TableCell>
                    </TableRow>
                )}
            </TableBody>
        </Table>
    )
}

export default DaycareNoMergerSettings
